import React, { useState } from 'react';
import api from '../services/api';
import { F1_DRIVERS, driverImgUrl } from '../constants/f1Drivers';

const TEAMS = [...new Set(F1_DRIVERS.map(d => d.team))];

export default function SeasonDriverModal({ onClose, onPicked }) {
  const [selected, setSelected]   = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [filter, setFilter]       = useState('');

  const displayed = filter
    ? F1_DRIVERS.filter(d => d.team === filter)
    : F1_DRIVERS;

  const handleConfirm = async () => {
    if (!selected) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/season-driver', { driver: selected });
      onPicked(res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur réseau.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(6px)' }}>
      <div className="relative w-full sm:max-w-2xl bg-zinc-900 border border-zinc-700 rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[92vh]">

        {/* Header — fixed */}
        <div className="px-5 pt-5 pb-4 border-b border-zinc-800 shrink-0">
          <div className="w-10 h-1 bg-zinc-700 rounded-full mx-auto mb-4 sm:hidden" />
          <p className="text-xs uppercase tracking-widest text-f1red font-bold mb-1">Saison 2026</p>
          <h2 className="text-xl font-black text-white">Choisis ton pilote de saison</h2>
          <p className="text-zinc-400 text-sm mt-1">
            Ses résultats à chaque GP t'apportent des points bonus.
            <strong className="text-white"> Ce choix est définitif.</strong>
          </p>
        </div>

        {confirming && selected ? (
          /* ── Confirmation step ── */
          <div className="p-6 flex flex-col items-center gap-5 text-center overflow-y-auto">
            <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 mx-auto"
              style={{ borderColor: selected.color, background: selected.color + '20' }}>
              <img src={driverImgUrl(selected.img)} alt={selected.name}
                className="w-full h-full object-cover object-top"
                onError={e => { e.target.style.display = 'none'; }} />
            </div>
            <div>
              <p className="text-xl font-black text-white">{selected.name}</p>
              <p className="text-sm font-bold mt-0.5" style={{ color: selected.color }}>{selected.team}</p>
            </div>
            <div className="bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-3 text-sm text-zinc-400 w-full max-w-sm">
              ⚠️ Tu ne pourras <strong className="text-white">plus changer</strong> de pilote après confirmation.
              Ses points depuis le début de saison seront ajoutés immédiatement.
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <div className="flex gap-3 w-full max-w-sm">
              <button onClick={() => { setConfirming(false); setError(''); }}
                className="flex-1 py-3 rounded-xl border border-zinc-700 text-zinc-300 font-bold hover:bg-zinc-800 transition-all">
                Retour
              </button>
              <button onClick={handleConfirm} disabled={loading}
                className="flex-1 py-3 rounded-xl font-black text-white transition-all disabled:opacity-60"
                style={{ background: selected.color }}>
                {loading ? '…' : 'Confirmer'}
              </button>
            </div>
          </div>
        ) : (
          /* ── Picker step ── */
          <>
            {/* Team filter — scrollable row */}
            <div className="px-4 pt-3 pb-2 flex gap-2 overflow-x-auto shrink-0 scrollbar-hide">
              <button onClick={() => setFilter('')}
                className={`text-xs px-3 py-1.5 rounded-full border font-bold whitespace-nowrap transition-all ${!filter ? 'bg-f1red border-f1red text-white' : 'border-zinc-700 text-zinc-400'}`}>
                Tous
              </button>
              {TEAMS.map(t => (
                <button key={t} onClick={() => setFilter(filter === t ? '' : t)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-bold whitespace-nowrap transition-all ${filter === t ? 'border-white text-white bg-zinc-700' : 'border-zinc-700 text-zinc-500'}`}>
                  {t}
                </button>
              ))}
            </div>

            {/* Driver grid — scrollable */}
            <div className="px-4 py-3 grid grid-cols-4 sm:grid-cols-5 gap-2.5 overflow-y-auto flex-1">
              {displayed.map(d => {
                const isSelected = selected?.code === d.code;
                return (
                  <button key={d.code} onClick={() => setSelected(d)}
                    className="flex flex-col items-center gap-1.5 p-2.5 rounded-2xl border transition-all active:scale-95"
                    style={isSelected
                      ? { borderColor: d.color, background: d.color + '20' }
                      : { borderColor: '#3f3f46', background: 'transparent' }}>
                    <div className="w-12 h-12 rounded-xl overflow-hidden"
                      style={{ background: d.color + '18' }}>
                      <img src={driverImgUrl(d.img)} alt={d.name}
                        className="w-full h-full object-cover object-top"
                        onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] font-black text-white leading-tight">
                        {d.name.split(' ').at(-1)}
                      </p>
                      <p className="text-[9px] font-bold" style={{ color: d.color }}>{d.code}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer — fixed */}
            <div className="px-5 pb-6 pt-3 border-t border-zinc-800 flex items-center justify-between gap-3 shrink-0">
              <button onClick={onClose}
                className="text-zinc-500 text-sm hover:text-zinc-300 transition-colors shrink-0">
                Pas maintenant
              </button>
              <button
                onClick={() => selected && setConfirming(true)}
                disabled={!selected}
                className="flex-1 py-3 rounded-xl font-black text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                style={{ background: selected ? selected.color : '#e10600' }}>
                {selected ? `Choisir ${selected.name.split(' ').at(-1)} →` : 'Sélectionne un pilote'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
